/**
 * Preferences utility for localStorage-based user preferences.
 * Used for onboarding state, medium preferences, and working style tracking.
 */

const STORAGE_KEY = 'brainstormy_profile';
const ONBOARDING_KEY = 'onboarding_complete';

/**
 * Get default preferences structure.
 */
export function getDefaultPreferences() {
  return {
    mediums_used: [],
    working_styles: {},
    intake_preferences: {},
    last_medium: null,
    last_working_style: null
  };
}

/**
 * Get all preferences from localStorage.
 * Returns default structure if nothing stored or on error.
 */
export function getPreferences() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : getDefaultPreferences();
  } catch (error) {
    console.error('Failed to read preferences:', error);
    return getDefaultPreferences();
  }
}

/**
 * Save/update preferences to localStorage.
 * Merges with existing preferences and tracks mediums/styles.
 *
 * @param {Object} updates - Preference updates to apply
 * @param {string} [updates.medium] - Medium used (novel)
 * @param {string} [updates.workingStyle] - Working style (explorer/architect)
 * @returns {Object} Updated preferences
 */
export function savePreferences(updates) {
  try {
    const current = getPreferences();
    const updated = { ...current, ...updates };

    // Track mediums used (no duplicates)
    if (updates.medium && !updated.mediums_used.includes(updates.medium)) {
      updated.mediums_used.push(updates.medium);
    }

    // Track last medium
    if (updates.medium) {
      updated.last_medium = updates.medium;
    }

    // Track working style per medium
    if (updates.medium && updates.workingStyle) {
      updated.working_styles[updates.medium] = updates.workingStyle;
      updated.last_working_style = updates.workingStyle;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (error) {
    console.error('Failed to save preferences:', error);
    return getPreferences();
  }
}

/**
 * Check if user has completed onboarding.
 *
 * NOTE: Onboarding is currently disabled. Always returns true.
 * When a new onboarding mechanism is implemented, this function
 * can be restored to check localStorage.
 */
export function hasCompletedOnboarding() {
  // ONBOARDING DISABLED: Always return true to skip onboarding flow
  // Original implementation:
  // try {
  //   return localStorage.getItem(ONBOARDING_KEY) === 'true';
  // } catch (error) {
  //   console.error('Failed to check onboarding status:', error);
  //   return false;
  // }
  return true;
}

/**
 * Mark onboarding as complete.
 */
export function completeOnboarding() {
  try {
    localStorage.setItem(ONBOARDING_KEY, 'true');
  } catch (error) {
    console.error('Failed to set onboarding complete:', error);
  }
}

/**
 * Clear onboarding completion (for testing/reset).
 */
export function clearOnboarding() {
  try {
    localStorage.removeItem(ONBOARDING_KEY);
  } catch (error) {
    console.error('Failed to clear onboarding:', error);
  }
}

/**
 * Clear all preferences (for testing/reset).
 */
export function clearAllPreferences() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ONBOARDING_KEY);
  } catch (error) {
    console.error('Failed to clear preferences:', error);
  }
}

/**
 * Check if localStorage is available.
 * Returns false in private browsing mode or if blocked.
 */
export function isStorageAvailable() {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
}
